import React, { type ComponentType } from 'react';

import { ExperienceTracker } from '@atlassian/jira-common-experience-tracking-viewing/src/view/experience-tracker-consumer/tracker-base/index.tsx';

import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import UFOLoadHold from '@atlassian/react-ufo/load-hold';
import type { SectionBoundaryId } from '../../../common/types.tsx';

export type BoundaryProps = {
	LoadingComponent: ComponentType<{}>;
	onSuccess: () => void;
	onError: (errorMessage: string) => void;
};

/**
 * @param {string} [boundaryId] should never change throughout the lifecycle of the component
 */
export const withBoundary = <T extends {}>({
	Component,
	boundaryId,
	Fallback,
	Skeleton,
}: {
	Component: ComponentType<T & BoundaryProps>;
	boundaryId: SectionBoundaryId;
	Fallback: ComponentType;
	Skeleton: ComponentType;
}) => {
	const SkeletonWithHold = () => (
		<>
			{/* eslint-disable-next-line jira/ufo/valid-labels */}
			<UFOLoadHold name={boundaryId} />
			{Skeleton && <Skeleton />}
		</>
	);

	return (props: T) => {
		return (
			// eslint-disable-next-line jira/ufo/valid-labels
			<UFOSegment name={boundaryId}>
				<JSErrorBoundary
					teamName="Helios - Reports"
					fallback={() => <Fallback />}
					id={boundaryId}
					packageName="jiraSpaAppsSoftwareSummary"
				>
					<ExperienceTracker location={boundaryId} parentProviders={['viewSummary']}>
						{({ onExperienceSuccess, onExperienceFailure }) => (
							<Component
								{...props}
								LoadingComponent={SkeletonWithHold}
								onSuccess={onExperienceSuccess}
								onError={(errorMessage: string) =>
									onExperienceFailure('', {
										failureReason: errorMessage,
									})
								}
							/>
						)}
					</ExperienceTracker>
				</JSErrorBoundary>
			</UFOSegment>
		);
	};
};
